import { useEffect, useMemo, useRef, useState } from 'react'
import type { Api } from '../api'
import type { ActAsCandidate } from '../api/types'
import { ApiError } from '../api/types'
import { Button, Loading, inputClass } from './ui'
import { WAITING_FOR_APPROVAL, entryReason } from '../pages/accessWords'

/**
 * What the count beside a person counts: permission rows under legacy, the
 * reports their own scopes open under the new model (entryState is set only
 * there, S17).
 */
function countWords(p: ActAsCandidate): string {
  const unit = p.entryState != null ? 'report' : 'grant'
  return `${p.grantRows} ${unit}${p.grantRows === 1 ? '' : 's'}`
}

/**
 * Under the new model, whether they may come in (S17): nothing when they may,
 * "Waiting for approval" when an approval would let them, otherwise "Cannot
 * enter", and why only when the list says (Admins; Development gets
 * CANNOT_ENTER alone, F19). Acting shows exactly that: their door.
 */
function EntryNote({ state }: { state?: string | null }) {
  if (!state || state === 'ALLOWED') return null
  if (WAITING_FOR_APPROVAL.has(state)) return <span className="block text-(--color-warn)">Waiting for approval</span>
  if (state === 'CANNOT_ENTER') return <span className="block text-(--color-overdue)">Cannot enter</span>
  return <span className="block text-(--color-overdue)" title={entryReason(state)}>Cannot enter: {entryReason(state)}</span>
}

/**
 * How long ago somebody last used the platform.
 *
 * NOT relativeTime(), which is built for a time in the FUTURE — it answers "how
 * long until this maintenance window lifts" and returns 'now' for anything
 * already past. Rendered through it, every person in this list read "seen now
 * ago", which is both wrong and nonsense.
 *
 * Coarse on purpose: the question here is "is this account live or dormant",
 * not what hour they signed in.
 */
function lastSeen(iso: string): string {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000)
  if (!Number.isFinite(days) || days < 0) return 'seen recently'
  if (days === 0) return 'seen today'
  if (days === 1) return 'seen yesterday'
  if (days < 30) return `seen ${days} days ago`
  const months = Math.round(days / 30)
  return months < 12 ? `seen ${months} month${months === 1 ? '' : 's'} ago` : 'seen over a year ago'
}

/**
 * LOGIN AS — be somebody else, completely.
 *
 * WHAT CHANGED, AND WHY THE NAME DID
 * ----------------------------------
 * View As rendered a REPORT through another person's permissions while the
 * session stayed the administrator's. Two identities, recombined at every call
 * site, and thirteen places where they came apart — a deactivated leaver's data
 * still rendering, a sidebar promising everything beside a grid showing almost
 * nothing, an admin seeing live figures where the person themselves saw a
 * fifteen-minute-old cache.
 *
 * Login As substitutes ONCE, in the API's single identity factory. After that
 * there is one identity, and "what would they see" has the only honest answer:
 * exactly this.
 *
 * WHY IT IS STILL SAFE
 * --------------------
 * It reaches nothing an administrator could not already reach. The capability
 * is read from the database on every request, the target must be an ACTIVE
 * principal, and every read is written to aud.Data_Access_Log_MBI with ActorOid
 * naming who was really at the keyboard — which is the answer to the objection
 * docs/ARCHITECTURE.md raised against impersonation.
 *
 * WHAT THIS FILE IS RESPONSIBLE FOR
 * ---------------------------------
 * One thing, and it is not security: making it impossible to FORGET. Under full
 * substitution the screen is somebody else's in every particular, so nothing in
 * the data can remind you. An administrator who reads 7.6M as their own total
 * when it is somebody else's has been misled by the tool, and the audit row
 * proving what happened will not undo the decision they took.
 *
 * Hence a banner that is present on every screen, cannot be dismissed without
 * leaving, and is loud enough to notice in a screenshot pasted into an email.
 */

/**
 * The persistent banner. Above everything, on every page, for as long as the
 * session is somebody else's.
 */
export function LoginAsBanner({
  target,
  actor,
  onExit,
}: {
  /** Whose session this is — the address every screen below is showing. */
  target: string
  /** Who is really signed in. Null only if the server has not said yet. */
  actor: string | null
  onExit: () => void
}) {
  return (
    // VIOLET, NOT NAVY, and that is a design rule rather than a preference.
    // Navy is the brand and marks anything interactive; if privilege were also
    // navy it would read as one more piece of chrome. This colour appears
    // nowhere else in the platform, so its only possible meaning is "you are
    // using a power an ordinary user does not have".
    //
    // no-print: screen chrome, carrying an Exit button that means nothing on
    // paper.
    <div
      role="status"
      className="no-print on-navy flex flex-wrap items-center justify-center gap-x-3 gap-y-1 bg-(--color-privileged) px-4 py-2 text-(length:--text-small) text-white"
    >
      <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-white/80" />
      <span className="font-semibold uppercase tracking-[0.06em]">Signed in as</span>
      <span className="font-mono font-semibold">{target}</span>
      {/* NAMING THE ACTOR IS THE POINT OF THE SECOND HALF.
          Under View As the platform still knew the administrator, and the
          sidebar footer said so. Now every identity on screen is the target's,
          so this line is the only place the real person appears. Without it an
          administrator glancing at their own screen has nothing at all to tell
          them whose it is. */}
      {actor && <span className="text-white/75">— you are {actor}</span>}
      <button
        type="button"
        onClick={onExit}
        className="rounded border border-white/40 px-2.5 py-0.5 text-(length:--text-micro) font-semibold transition-colors hover:bg-white/15"
      >
        Stop
      </button>
    </div>
  )
}

/**
 * The picker: choose somebody to be.
 *
 * TWO POPULATIONS, AND THE SECOND ONE IS A DIAGNOSIS.
 *
 * Acting resolves a target by object id, which exists only once somebody has
 * signed in at least once. Measured on the clone, 17 Sep 2026: 377 people
 * appear in the ERP permission table and 12 have ever signed in. Listing only
 * the selectable ones would remove 365 names — so an administrator searching
 * for the person they are about to grant access to would find nothing, with no
 * way to tell "has never opened MBI" from "I typed it wrong".
 *
 * So everybody is listed and the unreachable ones are dimmed, with the reason.
 * That is not two code paths: there is still exactly one way to act as
 * somebody, and it needs an oid.
 */
export function LoginAsPicker({
  api,
  current,
  onPick,
  onClose,
}: {
  api: Api
  /** The oid currently being acted as, so it can be marked. */
  current: string | null
  onPick: (target: { oid: string; email: string }) => void
  onClose: () => void
}) {
  const [search, setSearch] = useState('')
  const [people, setPeople] = useState<ActAsCandidate[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  // Escape closes. A modal that traps an administrator mid-task is worse than
  // no modal, and this one can appear over an unsaved maintenance form.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  useEffect(() => {
    let cancelled = false
    // Debounced: a query per keystroke against the permission view is a query
    // the database did not need to run.
    const t = setTimeout(() => {
      setError(null)
      api
        .actAsCandidates(search || undefined)
        .then((u) => !cancelled && setPeople(u))
        .catch((e: unknown) => {
          if (cancelled) return
          setPeople([])
          setError(
            e instanceof ApiError && e.failure.kind === 'forbidden'
              ? 'This account may not sign in as other users.'
              : 'Could not load the list of users.',
          )
        })
    }, 250)
    return () => {
      cancelled = true
      clearTimeout(t)
    }
  }, [api, search])

  // Split once rather than filtering twice in the render.
  const [available, unreachable] = useMemo(() => {
    const all = people ?? []
    return [all.filter((p) => p.canActAs), all.filter((p) => !p.canActAs)]
  }, [people])

  async function choose(p: ActAsCandidate) {
    if (!p.oid || busy) return
    setBusy(true)
    setError(null)
    try {
      // The audited switch. Everything after it rides the header, so this is the
      // only round trip the choice costs.
      const target = await api.beginActAs(p.oid)
      onPick({ oid: target.oid, email: target.email })
    } catch (e: unknown) {
      setError(
        e instanceof ApiError && 'explanation' in e.failure && typeof e.failure.explanation === 'string'
          ? e.failure.explanation
          : 'Could not sign in as that user.',
      )
      setBusy(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-start justify-center bg-black/40 p-6 pt-[12vh]"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Sign in as another user"
        className="w-full max-w-lg overflow-hidden rounded-lg border border-(--color-line) bg-(--color-surface) shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-(--color-line) px-4 py-3">
          <h2 className="text-(length:--text-lg) font-semibold tracking-[-0.01em] text-(--color-ink)">
            Sign in as another user
          </h2>
          <p className="mt-1 text-(length:--text-small) leading-relaxed text-(--color-ink-3)">
            The whole platform becomes theirs — their companies, their columns, their totals, their
            menu. You will see exactly what they see when they sign in, no more and no less. Every
            read is recorded against your name.
          </p>
        </div>

        <div className="px-4 py-3">
          <input
            ref={inputRef}
            className={inputClass}
            placeholder="Search by name or email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="max-h-[46vh] overflow-y-auto border-t border-(--color-line)">
          {people === null ? (
            <Loading label="Loading users…" />
          ) : people.length === 0 ? (
            <p className="px-5 py-6 text-(length:--text-body) text-(--color-ink-3)">
              {error ?? `Nobody matches “${search}”.`}
            </p>
          ) : (
            <>
              {available.map((p) => (
                <button
                  key={p.email}
                  type="button"
                  disabled={busy}
                  onClick={() => void choose(p)}
                  className={`flex w-full items-baseline justify-between gap-3 border-b border-(--color-line) px-4 py-2 text-left last:border-b-0 hover:bg-(--color-surface-3) disabled:cursor-wait ${
                    p.oid === current ? 'bg-(--color-accent)/10' : ''
                  }`}
                >
                  <span className="min-w-0">
                    <span className="block truncate text-(length:--text-body) font-medium text-(--color-ink)">
                      {p.displayName || p.email}
                    </span>
                    <span className="block truncate text-(length:--text-small) text-(--color-ink-3)">
                      {p.email}
                      {/* Their roles, because acting takes them on. Acting as a
                          System Admin hands you their write powers. */}
                      {p.roles && <span className="text-(--color-privileged)"> · {p.roles}</span>}
                    </span>
                  </span>
                  <span className="shrink-0 text-right text-(length:--text-small) text-(--color-ink-3)">
                    {/* Zero grants is not an error — it is what they see when
                        they sign in — but it is better known before than after. */}
                    <span className={p.grantRows === 0 ? 'text-(--color-warn)' : ''}>
                      {countWords(p)}
                    </span>
                    <EntryNote state={p.entryState} />
                    {p.lastSeenUtc && (
                      <span className="block text-(--color-ink-4)">{lastSeen(p.lastSeenUtc)}</span>
                    )}
                  </span>
                </button>
              ))}

              {unreachable.length > 0 && (
                <>
                  {/* THE SECOND HALF, AND WHY IT IS HERE.
                      These people exist in the ERP permission table and have
                      never opened MBI, so there is no object id to act as. They
                      are listed because their ABSENCE was the confusing thing:
                      365 of 377 names vanishing from a picker reads as a broken
                      search, not as an answer. */}
                  <div className="sticky top-0 border-y border-(--color-line) bg-(--color-surface-2) px-4 py-1.5 text-(length:--text-micro) font-semibold uppercase tracking-[0.07em] text-(--color-ink-3)">
                    {unreachable.some((p) => p.entryState != null) ? 'In Mindzer, never signed in · cannot sign in as' : 'Never signed in · cannot sign in as'}
                  </div>
                  {unreachable.map((p) => (
                    <div
                      key={p.email}
                      aria-disabled="true"
                      className="flex cursor-not-allowed items-baseline justify-between gap-3 border-b border-(--color-line) px-4 py-2 text-left opacity-55 last:border-b-0"
                    >
                      <span className="min-w-0">
                        <span className="block truncate text-(length:--text-body) font-medium text-(--color-ink-2)">
                          {p.displayName || p.email}
                        </span>
                        <span className="block truncate text-(length:--text-small) text-(--color-ink-3)">
                          {p.email}
                        </span>
                      </span>
                      <span className="shrink-0 text-right text-(length:--text-small) text-(--color-ink-3)">
                        {countWords(p)}
                        <EntryNote state={p.entryState} />
                      </span>
                    </div>
                  ))}
                </>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-(--color-line) bg-(--color-surface-2) px-4 py-3">
          <span className="text-(length:--text-small) text-(--color-ink-3)">
            {error ? (
              <span className="text-(--color-overdue)">{error}</span>
            ) : unreachable.length > 0 ? (
              `${available.length} you can sign in as · ${unreachable.length} have never signed in`
            ) : (
              `${available.length} you can sign in as`
            )}
          </span>
          <Button onClick={onClose}>Cancel</Button>
        </div>
      </div>
    </div>
  )
}
